import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  type ClientFilters,
  type CreateClientRequest,
  type CreateClientPlanRequest,
  type CreateInviteRequest,
  type CreatePaymentRequest,
  type CreatePlanRequest,
  type ConnectProviderRequest,
  type InvoiceFilters,
  type PauseRequest,
  type ProofStatus,
  type ReminderSettings,
  type ReviewProofRequest,
  type UpdateOrgRequest,
  type UploadProofRequest,
} from '@getpay93/api';
import { api } from '../lib/api';
import { qk } from './queryKeys';
import { usePaywall } from './paywall';

// ---- queries ----
export const useDashboardSummary = () => useQuery({ queryKey: qk.dashboardSummary, queryFn: () => api.dashboard.summary() });
export const useUpcoming = () => useQuery({ queryKey: qk.upcoming, queryFn: () => api.dashboard.upcoming() });
export const useRevenueMonthly = (months = 6) => useQuery({ queryKey: qk.revenueMonthly(months), queryFn: () => api.dashboard.revenueMonthly(months) });
export const useHealth = () => useQuery({ queryKey: qk.health, queryFn: () => api.dashboard.health() });
export const useOnboarding = () => useQuery({ queryKey: qk.onboarding, queryFn: () => api.dashboard.onboarding() });
export const useClients = (filters: ClientFilters) => useQuery({ queryKey: qk.clients(filters), queryFn: () => api.clients.list(filters) });
export const useClient = (id: string) => useQuery({ queryKey: qk.client(id), queryFn: () => api.clients.get(id), enabled: !!id });
export const useClientPlans = (clientId: string) => useQuery({ queryKey: qk.clientPlans(clientId), queryFn: () => api.clientPlans.listByClient(clientId), enabled: !!clientId });
export const usePlans = () => useQuery({ queryKey: qk.plans, queryFn: () => api.plans.list() });
export const useInvoices = (filters: InvoiceFilters) => useQuery({ queryKey: qk.invoices(filters), queryFn: () => api.invoices.list(filters) });
export const useProofs = (status?: ProofStatus) => useQuery({ queryKey: qk.proofs(status), queryFn: () => api.proofs.list(status) });
export const useMePlan = () => useQuery({ queryKey: qk.mePlan, queryFn: () => api.mePlan.get() });

// ---- mutations ----
export function useCreateClient() {
  const qc = useQueryClient();
  const { openUpgrade } = usePaywall();
  return useMutation({
    mutationFn: (body: CreateClientRequest) => api.clients.create(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clients'] }); qc.invalidateQueries({ queryKey: qk.mePlan }); qc.invalidateQueries({ queryKey: qk.onboarding }); },
    onError: (e) => { if (e instanceof ApiError && e.status === 402) openUpgrade(); },
  });
}
export function useAssignPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateClientPlanRequest) => api.clientPlans.assign(body),
    onSuccess: (_d, body) => { qc.invalidateQueries({ queryKey: qk.clientPlans(body.clientId) }); qc.invalidateQueries({ queryKey: ['invoices'] }); qc.invalidateQueries({ queryKey: qk.onboarding }); },
  });
}
export function useRegisterPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePaymentRequest) => api.payments.create(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoices'] }); qc.invalidateQueries({ queryKey: ['dashboard'] }); qc.invalidateQueries({ queryKey: qk.onboarding }); },
  });
}
export function useReviewProof() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; body: ReviewProofRequest }) => api.proofs.review(args.id, args.body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['proofs'] }); qc.invalidateQueries({ queryKey: ['invoices'] }); qc.invalidateQueries({ queryKey: ['dashboard'] }); },
  });
}
export function usePauseMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PauseRequest) => api.membership.pause(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clientPlans'] }); qc.invalidateQueries({ queryKey: ['invoices'] }); },
  });
}
export function useResumeMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { clientPlanId: string; note?: string }) => api.membership.resume(args.clientPlanId, { note: args.note }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clientPlans'] }); qc.invalidateQueries({ queryKey: ['invoices'] }); },
  });
}
export function useCreateInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateInviteRequest) => api.invites.create(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.invites }); qc.invalidateQueries({ queryKey: qk.onboarding }); },
  });
}
export function useSendReminder() {
  return useMutation({
    mutationFn: (args: { invoiceId: string; channel?: string }) =>
      api.http.post('/v1/notifications/send', { invoiceId: args.invoiceId, type: 'reminder', channel: args.channel ?? 'email' }),
  });
}

// ---- W2-B queries ----
export const useMonthlyReport = (year: number, month: number, enabled: boolean) =>
  useQuery({ queryKey: qk.report(year, month), queryFn: () => api.reports.monthly(year, month), enabled });
export const useReferrals = () => useQuery({ queryKey: qk.referrals, queryFn: () => api.referrals.mine() });
export const useReminderSettings = () => useQuery({ queryKey: qk.reminders, queryFn: () => api.settings.getReminders() });
export const useOrg = () => useQuery({ queryKey: qk.org, queryFn: () => api.org.get() });
export const useProviders = () => useQuery({ queryKey: qk.providers, queryFn: () => api.providers.list() });

// ---- W2-B mutations ----
export function useUpdateReminders() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body: ReminderSettings) => api.settings.updateReminders(body), onSuccess: () => qc.invalidateQueries({ queryKey: qk.reminders }) });
}
export function useUpdateOrg() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body: UpdateOrgRequest) => api.org.update(body), onSuccess: () => qc.invalidateQueries({ queryKey: qk.org }) });
}
export function useConnectProvider() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body: ConnectProviderRequest) => api.providers.connect(body), onSuccess: () => qc.invalidateQueries({ queryKey: qk.providers }) });
}
export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body: CreatePlanRequest) => api.plans.create(body), onSuccess: () => qc.invalidateQueries({ queryKey: qk.plans }) });
}
export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (args: { id: string; body: Partial<CreatePlanRequest> & { isActive?: boolean } }) => api.plans.update(args.id, args.body), onSuccess: () => qc.invalidateQueries({ queryKey: qk.plans }) });
}
export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.plans.remove(id), onSuccess: () => qc.invalidateQueries({ queryKey: qk.plans }) });
}
export function useRequestUpgrade() {
  return useMutation({ mutationFn: (note?: string) => api.mePlan.requestUpgrade(note) });
}

// ---- W3 (cliente) ----
export const useClientView = () => useQuery({ queryKey: qk.clientView, queryFn: () => api.clientView.get() });
export const useCoaches = () => useQuery({ queryKey: qk.coaches, queryFn: () => api.clientView.coaches() });
export const useMyProofs = () => useQuery({ queryKey: qk.myProofs, queryFn: () => api.proofs.listMine() });

export function useUploadProof() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UploadProofRequest) => api.proofs.upload(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.myProofs }); qc.invalidateQueries({ queryKey: qk.clientView }); },
  });
}
